import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Search, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { PageTitle, StatusBadge, EmptyState } from "@/components/brand";
import { Skeleton } from "@/components/ui/skeleton";
import { PasswordInput } from "@/pages/Login";

const CreateAdmin = ({ onDone }) => {
  const [f, setF] = useState({ full_name: "", email: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/super/admins", f); toast.success("Admin created"); setF({ full_name: "", email: "", password: "" }); onDone(); }
    catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <form onSubmit={submit} data-testid="create-admin-form" className="card-cr space-y-3 p-5">
      <p className="flex items-center gap-2 font-semibold"><UserPlus className="h-5 w-5 text-cr-gold" />Create admin account</p>
      <input data-testid="new-admin-name" className="input-cr" placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
      <input data-testid="new-admin-email" type="email" className="input-cr" placeholder="Admin email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      <PasswordInput testid="new-admin-password" placeholder="Password (8+ chars, letter & number)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      <button data-testid="create-admin-btn" className="btn-gold w-full">Create admin</button>
      <p className="text-xs text-cr-muted">The admin signs in at /admin/login.</p>
    </form>
  );
};

const AdminRow = ({ a, onDone }) => {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const coins = async () => {
    try { const { data } = await api.post(`/super/admins/${a.id}/coins`, { amount: Math.trunc(Number(amount)), reason }); toast.success(`Coins updated: ${data.coins}`); setAmount(""); setReason(""); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const status = async () => {
    const r = window.prompt(`Reason to ${a.status === "active" ? "suspend" : "reactivate"} ${a.email}`);
    if (!r || r.trim().length < 3) return;
    try { await api.post(`/super/admins/${a.id}/status`, { status: a.status === "active" ? "suspended" : "active", reason: r }); toast.success("Status updated"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid={`super-admin-row-${a.email}`} className="card-cr space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1"><p className="font-semibold">{a.full_name}</p><p className="text-xs text-cr-muted">{a.email} · created {dateTime(a.created_at)}</p>
          <p className="mt-1 text-xs">Admin code <b data-testid={`super-admin-code-${a.email}`} className="font-mono text-cr-gold">{a.admin_code}</b> · <span data-testid={`super-admin-users-${a.email}`}>{a.users_count} users</span></p></div>
        <StatusBadge status={a.status} />
        <span data-testid={`super-admin-coins-${a.email}`} className="font-display text-lg font-bold text-cr-gold">{a.coins.toLocaleString("en-IN")} coins</span>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row">
        <input data-testid={`super-coins-amount-${a.email}`} className="input-cr sm:w-36" placeholder="± coins" value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d-]/g, ""))} />
        <input data-testid={`super-coins-reason-${a.email}`} className="input-cr" placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button data-testid={`super-coins-btn-${a.email}`} disabled={!Number(amount) || reason.trim().length < 3} onClick={coins} className="btn-gold shrink-0 text-xs">Credit / Debit</button>
        <button data-testid={`super-status-btn-${a.email}`} onClick={status} className="btn-outline shrink-0 text-xs">{a.status === "active" ? "Suspend" : "Reactivate"}</button>
      </div>
    </div>
  );
};

const Ledger = () => {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  useEffect(() => { const t = setTimeout(() => api.get("/super/coin-ledger", { params: { q: q || undefined } }).then((r) => setRows(r.data)).catch(() => setRows([])), 250); return () => clearTimeout(t); }, [q]);
  return (
    <div className="mt-10">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><p className="text-lg font-semibold">Coin ledger</p>
        <div className="relative sm:w-72"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-cr-muted" /><input data-testid="coin-ledger-search" className="input-cr pl-10" placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} /></div></div>
      {!rows ? <Skeleton className="h-32 bg-cr-elevated" /> : rows.length === 0 ? <EmptyState title="No coin movements yet" /> : (
        <div className="overflow-x-auto rounded-xl border border-cr-line">
          <table data-testid="coin-ledger-table" className="w-full text-sm">
            <thead className="bg-cr-elevated text-left text-xs text-cr-muted"><tr>{["Time", "Ref", "Type", "From", "To", "Coins", "By", "Reason"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{rows.map((r) => <tr key={r.id} className="border-t border-cr-line text-xs"><td className="whitespace-nowrap px-4 py-2">{dateTime(r.created_at)}</td><td className="px-4 py-2 font-mono">{r.ref}</td><td className="px-4 py-2 text-cr-gold">{r.kind}</td><td className="px-4 py-2">{r.from_email}</td><td className="px-4 py-2">{r.to_email}</td><td className="px-4 py-2 font-bold">{r.amount}</td><td className="px-4 py-2">{r.actor_email}</td><td className="px-4 py-2 text-cr-muted">{r.reason}</td></tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default function SuperAdmin() {
  const { user } = useAuth();
  if (user?.role !== "super_admin") return <Navigate to="/super-admin/login" replace />;
  return <SuperAdminInner />;
}

function SuperAdminInner() {
  const [admins, setAdmins] = useState(null);
  const [key, setKey] = useState(0);
  const load = () => { api.get("/super/admins").then((r) => setAdmins(r.data)).catch((e) => { toast.error(apiError(e)); setAdmins([]); }); setKey((k) => k + 1); };
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="super-admin-page">
      <PageTitle title="Super Admin" subtitle="Create admins and credit them coins. Admins sell coins to players by UPI (1 coin = ₹1)." />
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <CreateAdmin onDone={load} />
        <div className="space-y-3">
          {!admins ? <Skeleton className="h-40 bg-cr-elevated" /> : admins.length === 0 ? <EmptyState testid="no-admins" title="No admins yet" text="Create your first admin account." /> :
            admins.map((a) => <AdminRow key={a.id} a={a} onDone={load} />)}
        </div>
      </div>
      <Ledger key={key} />
    </div>
  );
}
